import { useMemo, useState } from 'react';
import { Building2, HeartHandshake, History, Search, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { HUB_URL } from '@/lib/clerkSso';
import {
  useFermerIntervention,
  useJournalInterventions,
  useOfficinesConsole,
  useOuvrirIntervention,
  useOptionsOfficine,
  usePlateforme,
  type OfficineConsole,
} from '@/api/platform';

/**
 * Option payante « Gestion Sociale » d'une officine : l'interrupteur, et
 * l'adresse dédiée qui recevra les fiches (proposée au titulaire à l'envoi).
 */
function OptionGestionSociale({ officine }: { officine: OfficineConsole }) {
  const maj = useOptionsOfficine();
  const actif = officine.options.gestionSociale;
  const [email, setEmail] = useState(officine.options.socialEmail ?? '');
  const emailModifie = email.trim().toLowerCase() !== (officine.options.socialEmail ?? '');
  const emailValide = email.trim() === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  return (
    <div className={`mt-3 rounded-lg border p-3 ${actif ? 'border-emerald-200 bg-emerald-50/60' : 'border-slate-200 bg-slate-50'}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[12.5px] font-medium text-slate-800">
          <HeartHandshake size={14} className={actif ? 'text-emerald-600' : 'text-slate-400'} />
          Gestion Sociale
          <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wider text-slate-600">
            payant
          </span>
        </div>
        <button
          role="switch"
          aria-checked={actif}
          disabled={maj.isPending}
          onClick={() => maj.mutate({ pharmacyId: officine.id, gestionSociale: !actif })}
          title={actif ? 'Désactiver l’option' : 'Activer l’option'}
          className={`relative h-5 w-9 rounded-full transition-colors disabled:opacity-50 ${actif ? 'bg-emerald-500' : 'bg-slate-300'}`}
        >
          <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${actif ? 'left-[18px]' : 'left-0.5'}`} />
        </button>
      </div>
      {actif && (
        <div className="mt-2.5 flex items-center gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Adresse dédiée du service social"
            className="flex-1 min-w-0 px-2.5 py-1.5 text-[12px] rounded-md border border-slate-300 bg-white focus:outline-none focus:border-slate-500"
          />
          {emailModifie && (
            <Button
              size="sm"
              disabled={!emailValide}
              loading={maj.isPending}
              onClick={() => maj.mutate({ pharmacyId: officine.id, socialEmail: email.trim() || null })}
            >
              Enregistrer
            </Button>
          )}
        </div>
      )}
      {maj.isError && <p className="text-[11.5px] text-danger mt-1.5">{(maj.error as Error).message}</p>}
    </div>
  );
}

/**
 * LOT_ROLES_HUB — console de l'équipe PharmaGestion dans PharmaRH.
 *
 * On choisit une officine, on donne un motif, et l'on agit comme son titulaire
 * jusqu'à « Terminer l'intervention ». Tout est journalisé avec l'acteur réel.
 */
export default function ConsolePage() {
  const { data: plateforme } = usePlateforme();
  const officines = useOfficinesConsole();
  const journal = useJournalInterventions();
  const ouvrir = useOuvrirIntervention();
  const fermer = useFermerIntervention();
  const [onglet, setOnglet] = useState<'officines' | 'journal'>('officines');
  const [recherche, setRecherche] = useState('');
  const [cible, setCible] = useState<OfficineConsole | null>(null);
  const [motif, setMotif] = useState('Appel du titulaire');

  const filtrees = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    return (officines.data ?? []).filter(
      (o) => !q || [o.name, o.city ?? '', o.group.name, ...o.titulaires].some((x) => x.toLowerCase().includes(q)),
    );
  }, [officines.data, recherche]);

  const enCours = plateforme?.intervention ?? null;

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="max-w-5xl mx-auto px-8 py-8">
        <header className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-1">
              PharmaGestion · {plateforme?.role === 'SUPER_ADMIN' ? 'super admin' : 'admin PharmaRH'}
            </div>
            <h1 className="text-2xl font-semibold text-slate-900">Console PharmaRH</h1>
            <p className="text-[13px] text-slate-600 mt-1">
              Intervenir dans une officine, comme son titulaire. Chaque intervention et chaque modification sont tracées.
            </p>
          </div>
          <a href={`${HUB_URL}/admin`} className="text-[13px] text-slate-600 hover:text-slate-900 whitespace-nowrap">
            ← Retour au hub
          </a>
        </header>

        {enCours && (
          <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4 flex items-center gap-3">
            <ShieldCheck size={18} className="text-amber-700 flex-shrink-0" />
            <div className="flex-1 text-[13px] text-amber-900">
              Intervention en cours dans <strong>{enCours.pharmacy.name}</strong> — {enCours.reason}
            </div>
            <Button size="sm" onClick={() => (window.location.href = '/dashboard')}>Reprendre</Button>
            <Button size="sm" variant="danger" loading={fermer.isPending} onClick={() => fermer.mutate()}>
              Terminer
            </Button>
          </div>
        )}

        <div className="flex gap-1 mb-4 border-b border-slate-300">
          {([['officines', 'Officines', Building2], ['journal', 'Journal des interventions', History]] as const).map(
            ([k, label, Icon]) => (
              <button
                key={k}
                onClick={() => setOnglet(k)}
                className={`flex items-center gap-2 px-4 py-2.5 text-[13px] font-medium border-b-2 -mb-px ${
                  onglet === k ? 'border-primary text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <Icon size={14} /> {label}
              </button>
            ),
          )}
        </div>

        {onglet === 'officines' && (
          <>
            <div className="relative mb-4 max-w-sm">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                placeholder="Officine, ville, titulaire…"
                className="w-full pl-8 pr-3 py-2 text-[13px] rounded-lg border border-slate-300 bg-white focus:outline-none focus:border-slate-500"
              />
            </div>
            {officines.isLoading && <p className="text-[13px] text-slate-500">Chargement…</p>}
            {officines.isError && (
              <p className="text-[13px] text-danger">La liste des officines n'a pas pu être chargée.</p>
            )}
            <div className="grid gap-3 md:grid-cols-2">
              {filtrees.map((o) => (
                <div key={o.id} className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-[14px] text-slate-900 truncate">{o.name}</div>
                      <div className="text-[12px] text-slate-500">
                        {[o.city, `${o.employees} salarié${o.employees > 1 ? 's' : ''}`, o.group.name !== o.name ? `groupe ${o.group.name}` : null]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                      {o.titulaires.length > 0 && (
                        <div className="text-[12px] text-slate-600 mt-1 truncate">{o.titulaires.join(', ')}</div>
                      )}
                    </div>
                    <Button size="sm" variant="primary" onClick={() => setCible(o)}>Intervenir</Button>
                  </div>
                  <OptionGestionSociale officine={o} />
                </div>
              ))}
            </div>
          </>
        )}

        {onglet === 'journal' && (
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-2.5">Officine</th>
                  <th className="px-4 py-2.5">Par</th>
                  <th className="px-4 py-2.5">Motif</th>
                  <th className="px-4 py-2.5">Début</th>
                  <th className="px-4 py-2.5">Fin</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(journal.data ?? []).map((l) => (
                  <tr key={l.id}>
                    <td className="px-4 py-2.5 font-medium text-slate-900">{l.pharmacy.name}</td>
                    <td className="px-4 py-2.5 text-slate-600">{l.actorEmail}</td>
                    <td className="px-4 py-2.5 text-slate-600">{l.reason}</td>
                    <td className="px-4 py-2.5 text-slate-500 tabular-nums">{new Date(l.startedAt).toLocaleString('fr-FR')}</td>
                    <td className="px-4 py-2.5 text-slate-500 tabular-nums">
                      {l.endedAt ? new Date(l.endedAt).toLocaleString('fr-FR') : <span className="text-amber-700">en cours</span>}
                    </td>
                  </tr>
                ))}
                {journal.data?.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">Aucune intervention.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {cible && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={() => setCible(null)}>
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-slate-900 mb-1">Intervenir dans {cible.name}</h2>
            <p className="text-[13px] text-slate-600 mb-4">
              Vous agirez comme son titulaire jusqu'à « Terminer l'intervention ». Le motif est enregistré.
            </p>
            <label className="block text-[12px] font-medium text-slate-700 mb-1">Motif</label>
            <input
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              className="w-full px-3 py-2 text-[13px] rounded-lg border border-slate-300 focus:outline-none focus:border-slate-500"
            />
            {ouvrir.isError && <p className="text-[12px] text-danger mt-2">{(ouvrir.error as Error).message}</p>}
            <div className="flex justify-end gap-2 mt-5">
              <Button onClick={() => setCible(null)}>Annuler</Button>
              <Button
                variant="primary"
                loading={ouvrir.isPending}
                disabled={motif.trim().length < 3}
                onClick={() => ouvrir.mutate({ pharmacyId: cible.id, reason: motif.trim() })}
              >
                Intervenir
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
